import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { of, Subject, Subscription, timer } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { SafetyRoundBusinessCandidate, SafetyRoundTemplateAssignment } from '../safety-round.models';
import { FlexiSafetyRoundTemplateService } from './safety-round-template.service';

/**
 * Right-hand drawer for managing template assignments. Used
 * from the template library and from the editor's "assigned businesses" card.
 *
 * The candidate list is searched server side (at most 50 per search), so the
 * ticked businesses are kept by id across searches. Businesses that already
 * have the template are removed through the separate assigned-business list.
 */
@Component({
  selector: 'flexi-safety-round-assign-drawer',
  templateUrl: './safety-round-assign-drawer.component.html',
  styleUrls: ['./safety-round-assign-drawer.component.scss'],
  standalone: false,
})
export class SafetyRoundAssignDrawerComponent implements OnInit, OnDestroy {
  @Input({ required: true }) templateId = 0;
  @Input() templateName = '';
  @Input() canAssign = true;
  /** Emits true after any assignment change, including removal followed by Close. */
  @Output() closed = new EventEmitter<boolean>();

  visible = true;
  search = '';
  candidates: SafetyRoundBusinessCandidate[] = [];
  loading = false;
  loadFailed = false;
  /** Ticked business ids, with their names for the summary line. */
  ticked = new Map<number, string>();
  makeDefault = false;
  saving = false;
  assignments: SafetyRoundTemplateAssignment[] = [];
  assignmentsLoading = false;
  assignmentsLoadFailed = false;
  private changed = false;

  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();
  private searchDelay = new Subscription();

  constructor(
    private api: FlexiSafetyRoundTemplateService,
    private notifications: NotificationService,
    private translate: TranslateService,
    private confirmation: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.loadAssignments();
    if (!this.canAssign) {
      return;
    }
    this.search$
      .pipe(
        switchMap(search => {
          this.loading = true;
          this.loadFailed = false;
          return this.api.businessCandidates(this.templateId, search).pipe(
            // Caught per search, so one failure does not end the search stream.
            catchError(() => {
              this.loadFailed = true;
              return of([] as SafetyRoundBusinessCandidate[]);
            }),
            finalize(() => (this.loading = false)),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(rows => (this.candidates = rows));
    this.search$.next('');
  }

  ngOnDestroy(): void {
    this.searchDelay.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchChanged(): void {
    this.searchDelay.unsubscribe();
    this.searchDelay = timer(300)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.search$.next(this.search.trim()));
  }

  isTicked(candidate: SafetyRoundBusinessCandidate): boolean {
    return candidate.HasTemplate || this.ticked.has(candidate.BusinessId);
  }

  toggle(candidate: SafetyRoundBusinessCandidate): void {
    if (!this.canAssign || this.saving || candidate.HasTemplate) {
      return;
    }
    if (this.ticked.has(candidate.BusinessId)) {
      this.ticked.delete(candidate.BusinessId);
    } else {
      this.ticked.set(candidate.BusinessId, candidate.BusinessName);
    }
  }

  submit(): void {
    if (!this.canAssign || !this.ticked.size || this.saving) {
      return;
    }
    this.saving = true;
    this.api
      .assign(this.templateId, { BusinessIds: [...this.ticked.keys()], MakeDefault: this.makeDefault })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
          this.saving = false;
          this.close(true);
        },
        // The interceptor reports the failure; keep the selection available for retry.
        error: () => {},
      });
  }

  loadAssignments(): void {
    this.assignmentsLoading = true;
    this.assignmentsLoadFailed = false;
    this.api.businesses(this.templateId)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.assignmentsLoading = false)))
      .subscribe({
        next: rows => (this.assignments = rows),
        error: () => (this.assignmentsLoadFailed = true),
      });
  }

  confirmUnassign(assignment: SafetyRoundTemplateAssignment): void {
    if (this.saving) {
      return;
    }
    this.confirmation.confirm({
      key: 'fvrAssign',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_SAFETY_ROUNDS.EDITOR.CONFIRM_UNASSIGN', { name: assignment.BusinessName }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        if (this.saving || !this.visible) {
          return;
        }
        this.saving = true;
        this.searchDelay.unsubscribe();
        this.api.unassign(this.templateId, assignment.BusinessId)
          .pipe(takeUntil(this.destroy$), finalize(() => (this.saving = false)))
          .subscribe({
            next: () => {
              this.changed = true;
              this.assignments = this.assignments.filter(row => row.BusinessId !== assignment.BusinessId);
              this.ticked.delete(assignment.BusinessId);
              if (this.canAssign) {
                this.search$.next(this.search.trim());
              }
              this.notifications.success('UI_NOTIFICATIONS.DELETED');
            },
            // The interceptor reports the failure; keep the row available for retry.
            error: () => {},
          });
      },
    });
  }

  cancel(): void {
    this.close(false);
  }

  visibleChanged(visible: boolean): void {
    if (!visible) {
      this.close(false);
    }
  }

  private close(saved: boolean): void {
    if (this.saving) {
      return;
    }
    this.visible = false;
    this.closed.emit(saved || this.changed);
  }
}
